import React, { useCallback, useMemo, useState } from 'react';
import type {
  AiModelConfig,
  AiModelOption,
  AiPromptTemplate,
  AiTestProject,
  AiTestRecipeAttribute,
} from './types';
import { createTesterRun } from './api';
import ModelPicker from './ModelPicker';
import PromptPicker from './PromptPicker';
import RecipeTargetPicker, { type RecipeTargetSelection } from './RecipeTargetPicker';

interface RecipeAttributesFormProps {
  projects: AiTestProject[];
  models: AiModelOption[];
  prompts: AiPromptTemplate[];
  onPromptsChanged: () => Promise<void> | void;
  onRunStarted: () => Promise<void> | void;
}

interface AttrPromptState {
  prompt: AiPromptTemplate | null;
  content: string;
}

const AttrPromptFields: React.FC<{
  attr: AiTestRecipeAttribute;
  required: boolean;
  state: AttrPromptState | undefined;
  prompts: AiPromptTemplate[];
  onPrompt: (apiName: string, prompt: AiPromptTemplate | null) => void;
  onContent: (apiName: string, content: string) => void;
  onPromptsChanged: () => Promise<void> | void;
  disabled?: boolean;
}> = ({ attr, required, state, prompts, onPrompt, onContent, onPromptsChanged, disabled }) => {
  const handleSelect = useCallback(
    (prompt: AiPromptTemplate | null) => onPrompt(attr.api_name, prompt),
    [attr.api_name, onPrompt]
  );
  const handleContent = useCallback(
    (content: string) => onContent(attr.api_name, content),
    [attr.api_name, onContent]
  );
  return (
    <PromptPicker
      label={required ? 'Prompt' : 'Prompt override (optional)'}
      generatorKey="recipe_attributes"
      stepKey={attr.api_name}
      prompts={prompts}
      selectedId={state?.prompt?.id ?? null}
      blobFilename={state?.prompt?.blob_filename || ''}
      onSelect={handleSelect}
      onContentChange={handleContent}
      onPromptsChanged={onPromptsChanged}
      disabled={disabled}
    />
  );
};

const RecipeAttributesForm: React.FC<RecipeAttributesFormProps> = ({
  projects,
  models,
  prompts,
  onPromptsChanged,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [target, setTarget] = useState<RecipeTargetSelection | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [optionsPerLevel, setOptionsPerLevel] = useState(3);
  const [appendToProject, setAppendToProject] = useState(true);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({});
  const [promptState, setPromptState] = useState<Record<string, AttrPromptState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const attributes = useMemo(
    () => target?.combination.attributes || [],
    [target]
  );

  const handleTargetChange = (next: RecipeTargetSelection | null) => {
    setTarget(next);
    setSelectedIds((next?.combination.attributes || []).map((attr) => attr.id));
  };

  const toggleAttr = (id: number) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const allSelected = attributes.length > 0 && selectedIds.length === attributes.length;

  const selectedAttrs = useMemo(
    () => attributes.filter((attr) => selectedIds.includes(attr.id)),
    [attributes, selectedIds]
  );

  const missingPromptAttrs = selectedAttrs.filter((attr) => {
    if (attr.has_default_prompt) return false;
    const state = promptState[attr.api_name];
    const inline = (state?.content || '').trim();
    return !inline && !state?.prompt;
  });

  const canRun = Boolean(
    project && target && selectedAttrs.length > 0 && missingPromptAttrs.length === 0
  );

  const handlePromptSelect = useCallback((apiName: string, prompt: AiPromptTemplate | null) => {
    setPromptState((prev) => ({
      ...prev,
      [apiName]: {
        prompt,
        content: prev[apiName]?.content || '',
      },
    }));
  }, []);

  const handlePromptContent = useCallback((apiName: string, content: string) => {
    setPromptState((prev) => {
      const current = prev[apiName];
      if (current?.content === content) return prev;
      return {
        ...prev,
        [apiName]: {
          prompt: current?.prompt || null,
          content,
        },
      };
    });
  }, []);

  const handleRun = async () => {
    if (!project || !target) {
      setError('Select a project, recipe card, and combination first.');
      return;
    }
    if (selectedAttrs.length === 0) {
      setError('Tick at least one attribute to generate.');
      return;
    }
    if (missingPromptAttrs.length > 0) {
      setError(
        `Add a prompt for: ${missingPromptAttrs.map((attr) => attr.attribute_name).join(', ')}.`
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const promptOverrides: Record<string, Record<string, unknown>> = {};
    for (const attr of selectedAttrs) {
      const state = promptState[attr.api_name];
      const inline = (state?.content || '').trim();
      if (inline || state?.prompt) {
        promptOverrides[attr.api_name] = {
          prompt_inline: inline || undefined,
          prompt_id: inline ? undefined : state?.prompt?.id || undefined,
          blob_filename: inline || state?.prompt?.id
            ? undefined
            : state?.prompt?.blob_filename || undefined,
        };
      }
    }
    try {
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: 'recipe_attributes',
        blueprint_id: target.blueprint.id,
        tab_id: target.tab.id,
        combination_id: target.combination.id,
        attribute_ids: selectedAttrs.map((attr) => attr.id),
        options_per_level: optionsPerLevel,
        variable_keys: [
          'market', 'jtbd', 'tension', 'benefit', 'brand_choice', 'opportunity_space', 'markets',
        ],
        model_id: modelId || undefined,
        model_config: modelConfig,
        prompt_overrides: promptOverrides,
        append_to_project: appendToProject,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  const promptsForAttr = (attr: AiTestRecipeAttribute) => (
    prompts.filter((p) => (p.step_key || '') === attr.api_name || (p.step_key || '') === (attr.attribute_key || ''))
  );

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value ? Number(e.target.value) : '');
            setTarget(null);
            setSelectedIds([]);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <RecipeTargetPicker
        projectId={projectId}
        value={target}
        onChange={handleTargetChange}
        disabled={submitting}
      />

      {attributes.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12px] font-medium text-[#626262]">
              Attributes to generate
            </label>
            <button
              type="button"
              onClick={() => setSelectedIds(allSelected ? [] : attributes.map((attr) => attr.id))}
              className="text-[12px] text-[#3da3d4]"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          </div>
          <div className="w-full rounded-md border border-[#D1D1D1] bg-white">
            {attributes.map((attr, index) => {
              const selected = selectedIds.includes(attr.id);
              return (
                <label
                  key={attr.id}
                  className={`flex items-start gap-2 px-3 py-2.5 text-sm whitespace-normal break-words ${
                    index > 0 ? 'border-t border-[#E8E8E8]' : ''
                  } ${selected ? 'bg-[#E8F4FB]' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected}
                    disabled={submitting}
                    onChange={() => toggleAttr(attr.id)}
                  />
                  <span>
                    <span className="text-[#0C0B0B]">{attr.attribute_name}</span>
                    {attr.is_user_added && (
                      <span className="ml-1.5 text-[11px] font-medium text-[#8A8A8A]">User</span>
                    )}
                    {!attr.has_default_prompt && (
                      <span className="ml-1.5 text-[11px] font-medium text-amber-700">
                        no default prompt
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {selectedAttrs.map((attr) => {
        const required = !attr.has_default_prompt;
        return (
          <details
            key={attr.id}
            open={required}
            className="rounded-md border border-[#E8E8E8] p-3 space-y-2"
          >
            <summary className="cursor-pointer text-[12px] font-medium text-[#0C0B0B]">
              {attr.attribute_name}
              {required ? (
                <span className="ml-2 font-normal text-amber-700">Prompt required</span>
              ) : (
                <span className="ml-2 font-normal text-[#8A8A8A]">optional prompt override</span>
              )}
            </summary>
            <div className="mt-2">
              <AttrPromptFields
                attr={attr}
                required={required}
                state={promptState[attr.api_name]}
                prompts={promptsForAttr(attr)}
                onPrompt={handlePromptSelect}
                onContent={handlePromptContent}
                onPromptsChanged={onPromptsChanged}
                disabled={submitting}
              />
            </div>
          </details>
        );
      })}

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Options per level
        </label>
        <input
          type="number"
          min={1}
          max={10}
          value={optionsPerLevel}
          onChange={(e) => setOptionsPerLevel(Number(e.target.value) || 3)}
          disabled={submitting}
          className="w-24 rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        />
      </div>

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey="recipe_attributes"
        disabled={submitting}
      />

      <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
        <input
          type="checkbox"
          checked={appendToProject}
          onChange={(e) => setAppendToProject(e.target.checked)}
        />
        Add options to the project workspace
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !canRun}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Starting…' : 'Run Recipe Attributes'}
      </button>
    </div>
  );
};

export default RecipeAttributesForm;
